import { mapLocationDataToOption } from '@trustoo/components/searchPicker/funcs/mapLocationDataToOption'
import { SearchPickerOption } from '@trustoo/components/searchPicker/SearchPickerContext'
import { GeoIdLocation } from '@trustoo/types/api/controllers/geo'
import SearchLocationInput, { findLocation } from 'components/ui/SearchLocationInput'
import { useEffect, useRef, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

// A postal code covers a handful of places at most, so this is "all of them" rather than a page.
const PLACES_PER_POSTAL_CODE = 99

// The postal code field commits every keystroke, and each set of places remounts the picker.
const POSTAL_CODE_DEBOUNCE_MS = 300

interface PostalCodePlaces {
    postalCode: string
    options: SearchPickerOption[]
}

const NO_PLACES: PostalCodePlaces = { postalCode: '', options: [] }

interface ProviderCityInputProps {
    postalCode?: string
    countryCode?: string | false
    value?: string
    className?: string
    onChangeLocation: (location: GeoIdLocation) => void
}

/**
 * City picker that, once the admin edits the provider's postal code, opens on the places sharing
 * the new code, so its city can be picked without searching. The postal code the provider was
 * loaded with already goes with the saved city, so until it is edited the list starts empty and
 * nothing is fetched. Typing always runs a normal city search — the postal-code places are only
 * what the list rests on.
 */
const ProviderCityInput = ({
    postalCode,
    countryCode = false,
    value,
    className,
    onChangeLocation,
}: ProviderCityInputProps) => {
    const [places, setPlaces] = useState<PostalCodePlaces>(NO_PLACES)
    const [postalCodeDigits] = useDebounceValue(
        postalCode ? postalCode.replace(/\D/g, '') : '',
        POSTAL_CODE_DEBOUNCE_MS,
    )
    const [loadedPostalCodeDigits] = useState(postalCodeDigits)
    // Stays set once the code has changed, so going back to the loaded code still replaces the
    // places of the code typed in between.
    const hasEditedPostalCode = useRef(false)

    useEffect(() => {
        if (!hasEditedPostalCode.current) {
            if (postalCodeDigits === loadedPostalCodeDigits) {
                return
            }
            hasEditedPostalCode.current = true
        }

        if (!postalCodeDigits) {
            setPlaces(NO_PLACES)
            return
        }

        let isCurrent = true
        findLocation(postalCodeDigits, countryCode, PLACES_PER_POSTAL_CODE).then(({ results }) => {
            if (isCurrent) {
                setPlaces({
                    postalCode: postalCodeDigits,
                    options: results.map(mapLocationDataToOption),
                })
            }
        })

        return () => {
            isCurrent = false
        }
    }, [postalCodeDigits, loadedPostalCodeDigits, countryCode])

    return (
        // The input reads its resting places once, at mount, so a new set of places only reaches
        // it through a remount. The key is the postal code those places belong to rather than the
        // one being typed: it changes when the places do, so the picker cannot be remounted onto
        // the previous code's list while the request for the new one is still running.
        <SearchLocationInput
            key={places.postalCode}
            className={className}
            countryCode={countryCode}
            value={value}
            initialOptions={places.options}
            onChangeLocation={onChangeLocation}
        />
    )
}

export default ProviderCityInput
